import { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';

import { useAdmin } from '../hooks/useAdmin';

import { supabase } from '../lib/supabase';
import { captureException } from '../lib/sentry';
import type { Database } from '../types/database';


type Profile = Database['public']['Tables']['profiles']['Row'];
type Project = Database['public']['Tables']['projects']['Row'];

interface UserWithStats extends Profile {
  org_count: number;
  project_count: number;
}

interface ProjectWithDetails extends Project {
  organization_name: string;
  creator_name: string | null;
  member_count: number;
  completion_percentage: number;
}

// Sentry test button component - only visible to specific admin
function ErrorButton() {
  return (
    <button
      onClick={() => {
        throw new Error('Sentry test error - Admin button clicked! 🔥');
      }}
      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors font-medium"
    >
      🔥 Test Sentry Error Tracking
    </button>
  );
}

export function AdminPage() {
  const { user, signOut } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'users' | 'projects'>('users');
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [projects, setProjects] = useState<ProjectWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ userId: string; email: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (!adminLoading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, adminLoading, navigate]);

  // Load users data
  useEffect(() => {
    if (!isAdmin) return;

    const loadUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all data in parallel for better performance
        const [
          { data: profiles, error: profilesError },
          { data: memberships, error: membershipsError },
          { data: projectCounts, error: projectsError }
        ] = await Promise.all([
          supabase
            .from('profiles')
            .select('id, email, full_name, avatar_url, affiliation, is_admin, created_at, updated_at')
            .order('created_at', { ascending: false }),
          supabase
            .from('organization_members')
            .select('user_id'),
          supabase
            .from('projects')
            .select('created_by')
            .is('deleted_at', null)
        ]);

        if (profilesError) throw profilesError;
        if (membershipsError) throw membershipsError;
        if (projectsError) throw projectsError;

        // Combine data
        const usersWithStats: UserWithStats[] = (profiles || []).map(profile => {
          const orgCount = memberships?.filter(m => m.user_id === profile.id).length || 0;
          const projectCount = projectCounts?.filter(p => p.created_by === profile.id).length || 0;

          return {
            ...profile,
            org_count: orgCount,
            project_count: projectCount,
          };
        });

        setUsers(usersWithStats);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Error loading users'); captureException(error, { extra: { context: 'AdminPage users load' } });
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [isAdmin]);

  // Load projects data
  useEffect(() => {
    if (!isAdmin) return;

    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all data in parallel for better performance
        const [
          { data: projectsData, error: projectsError },
          { data: orgsData, error: orgsError },
          { data: creatorsData, error: creatorsError },
          { data: membersData, error: membersError },
          { data: completionData, error: completionError }
        ] = await Promise.all([
          supabase
            .from('projects')
            .select('*')
            .is('deleted_at', null)
            .order('created_at', { ascending: false }),
          supabase
            .from('organizations')
            .select('*'),
          supabase
            .from('profiles')
            .select('id, email, full_name'),
          supabase
            .from('organization_members')
            .select('organization_id'),
          supabase
            .from('project_module_completion')
            .select('project_id, completed')
        ]);

        if (projectsError) throw projectsError;
        if (orgsError) throw orgsError;
        if (creatorsError) throw creatorsError;
        if (membersError) throw membersError;
        if (completionError) throw completionError;

        // Combine data
        const projectsWithDetails: ProjectWithDetails[] = (projectsData || []).map(project => {
          const org = orgsData?.find(o => o.id === project.organization_id);
          const creator = creatorsData?.find(c => c.id === project.created_by);
          const memberCount = membersData?.filter(m => m.organization_id === project.organization_id).length || 0;

          // Calculate completion percentage
          const projectModules = completionData?.filter(c => c.project_id === project.id) || [];
          const completedModules = projectModules.filter(m => m.completed).length;
          const completionPercentage = projectModules.length > 0
            ? Math.round((completedModules / projectModules.length) * 100)
            : 0;

          return {
            ...project,
            organization_name: org?.name || 'Unknown',
            creator_name: creator?.full_name || creator?.email || 'Unknown',
            member_count: memberCount,
            completion_percentage: completionPercentage,
          };
        });

        setProjects(projectsWithDetails);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Error loading projects'); captureException(error, { extra: { context: 'AdminPage projects load' } });
        setError('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [isAdmin]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    setDeleteConfirm({ userId, email });
  };

  const confirmDeleteUser = async () => {
    if (!deleteConfirm) return;

    setDeleting(true);
    setError(null);

    try {
      // First validate/refresh the token by calling getUser
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('Not authenticated');
      }

      // Get the refreshed session for authorization
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Session not available');
      }

      // Call the Edge Function to delete user
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/delete-user-admin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            userId: deleteConfirm.userId
          }),
        }
      );

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error(`Server error (${response.status}): Unable to parse response`);
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || `Failed to delete user (${response.status})`);
      }

      // Remove user from local state
      setUsers(users.filter(u => u.id !== deleteConfirm.userId));
      setDeleteConfirm(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error deleting user'); captureException(error, { extra: { userId: deleteConfirm?.userId, context: 'AdminPage delete user' } });
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  if (adminLoading || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-red-600">Admin Panel</h1>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
              >
                ← Back to Dashboard
              </button>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{user?.email}</span>
              <button
                onClick={handleSignOut}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin Tools - Only visible to admins */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Newsletter Management */}
          <div className="p-4 bg-blue-50 border-2 border-blue-400 rounded-lg">
            <p className="text-sm font-medium text-blue-800 mb-2">
              📧 Newsletter Management
            </p>
            <p className="text-xs text-blue-700 mb-3">
              Send newsletter broadcasts to all subscribed users. Manage subscribers and view email analytics.
            </p>
            <button
              onClick={() => navigate('/admin/newsletter')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium"
            >
              📧 Open Newsletter Admin
            </button>
          </div>

          {/* Analytics & Reports */}
          <div className="p-4 bg-green-50 border-2 border-green-400 rounded-lg">
            <p className="text-sm font-medium text-green-800 mb-2">
              Analytics & Reports
            </p>
            <p className="text-xs text-green-700 mb-3">
              View comprehensive analytics on Discovery and Sector Map usage across all organizations and users.
            </p>
            <button
              onClick={() => navigate('/admin/reports')}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors font-medium"
            >
              Open Reports Dashboard
            </button>
          </div>

          {/* Sentry Test Button - Only visible to monty.sharma@gmail.com */}
          {user?.email === 'monty.sharma@gmail.com' && (
            <div className="p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
              <p className="text-sm font-medium text-yellow-800 mb-2">
                🧪 Sentry Integration Test (Only visible to you):
              </p>
              <p className="text-xs text-yellow-700 mb-3">
                Click the button below to trigger a test error and verify Sentry is capturing errors correctly.
              </p>
              <ErrorButton />
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`${
                activeTab === 'users'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Users ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('projects')}
              className={`${
                activeTab === 'projects'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Projects ({projects.length})
            </button>
          </nav>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading {activeTab}...</p>
          </div>
        ) : (
          <>
            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Full Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Affiliation
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Admin
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Organizations
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Projects Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.full_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.affiliation || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.is_admin ? (
                            <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded">
                              Admin
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.org_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.project_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => navigate(`/admin/user/${user.id}`)}
                              className="text-blue-600 hover:text-blue-700 hover:underline"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              className="text-red-600 hover:text-red-700 hover:underline font-semibold"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Project Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Organization
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Creator
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Team Members
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Completion
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Updated
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {projects.map((project) => (
                      <tr key={project.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {project.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {project.organization_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {project.creator_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {project.member_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-green-600 h-2 rounded-full"
                                style={{ width: `${project.completion_percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs">{project.completion_percentage}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => navigate(`/admin/project/${project.id}`)}
                            className="text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => !deleting && setDeleteConfirm(null)}
        >
          <div
            className="bg-white rounded-lg p-8 max-w-md w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="text-red-500 text-5xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Delete User</h3>
              <p className="text-gray-600">
                Are you sure you want to delete the user:
              </p>
              <p className="text-lg font-semibold text-gray-900 mt-2">
                {deleteConfirm.email}
              </p>
              <p className="text-sm text-red-600 mt-4">
                This action cannot be undone. All user data, projects, and memberships will be permanently deleted.
              </p>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteUser}
                disabled={deleting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete User'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
